import type {
  Application,
  ApplicationLaunchTarget,
  ApplicationPlatform,
} from "@ark/contracts";

import {
  isBundledDesktopApplication,
  isDesktopRuntime,
  openExternalURL,
} from "./desktopRuntime";

export function currentPlatform(): ApplicationPlatform {
  if (isDesktopRuntime()) return "desktop";
  const ua = navigator.userAgent.toLowerCase();
  if (/iphone|ipad|ipod/.test(ua)) return "ios";
  if (/android/.test(ua)) return "android";
  return "web";
}

export function innerTarget(
  app: Application,
): ApplicationLaunchTarget | undefined {
  if (isDesktopRuntime() && !isBundledDesktopApplication(app)) {
    return undefined;
  }
  return app.launchTargets
    .filter(
      (target) =>
        target.kind === "inner" &&
        (target.platform === currentPlatform() || target.platform === "web"),
    )
    .sort(targetOrder)[0];
}

// matchApplication 取 mountPath 为当前路径前缀且最长的应用（支持 remote 内嵌套路由）。
export function matchApplication(
  apps: Application[],
  pathname: string,
): { app: Application; target: ApplicationLaunchTarget } | undefined {
  let best: { app: Application; target: ApplicationLaunchTarget } | undefined;
  for (const app of apps) {
    const target = innerTarget(app);
    const mountPath = target?.mountPath;
    if (
      target &&
      mountPath &&
      (pathname === mountPath || pathname.startsWith(`${mountPath}/`)) &&
      (!best || mountPath.length > (best.target.mountPath?.length ?? 0))
    ) {
      best = { app, target };
    }
  }
  return best;
}

export function primaryTarget(
  app: Application,
): ApplicationLaunchTarget | undefined {
  const platform = currentPlatform();
  const sorted = app.launchTargets.slice().sort(targetOrder);
  if (platform === "desktop") {
    if (isBundledDesktopApplication(app)) return innerTarget(app);
    return externalBrowserTarget(sorted);
  }
  const preferred = sorted.find(
    (target) => target.targetKey === app.launchPreference?.effectiveTargetKey,
  );
  if (preferred) return preferred;
  return (
    sorted.find((target) => target.platform === platform && target.isPrimary) ??
    sorted.find((target) => target.platform === "web" && target.isPrimary) ??
    sorted.find((target) => target.platform === platform) ??
    sorted.find((target) => target.platform === "web") ??
    sorted[0]
  );
}

export function visibleTargets(app: Application): ApplicationLaunchTarget[] {
  if (!isDesktopRuntime()) return app.launchTargets;
  if (isBundledDesktopApplication(app)) {
    const target = innerTarget(app);
    return target ? [target] : [];
  }
  const target = externalBrowserTarget(
    app.launchTargets.slice().sort(targetOrder),
  );
  return target ? [target] : [];
}

export async function openApplicationTarget(
  navigate: (path: string) => unknown,
  app: Application,
  target: ApplicationLaunchTarget,
  requestedPath?: string,
): Promise<void> {
  if (isDesktopRuntime()) {
    if (target.kind === "inner" && target.mountPath) {
      navigate(requestedPath || target.mountPath);
      return;
    }
    if (
      (target.kind === "external_link" || target.kind === "iframe") &&
      target.url
    ) {
      return openExternalURL(target.url);
    }
    if (target.kind === "native_link" && target.fallbackUrl) {
      return openExternalURL(target.fallbackUrl);
    }
    throw new Error(`应用「${app.name}」没有可用的桌面入口`);
  }
  if (target.kind === "inner" && target.mountPath) {
    navigate(requestedPath || target.mountPath);
    return;
  }
  if (target.kind === "iframe") {
    navigate(`/apps/launch/${app.id}/${target.id}`);
    return;
  }
  if (target.kind === "external_link" && target.url) {
    const url = standaloneTargetURL(app, target, requestedPath);
    if (target.targetKey === "standalone" && target.url.startsWith("/open/")) {
      const opened = window.open(url, "_blank");
      if (opened) opened.opener = null;
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
    return;
  }
  if (target.kind === "native_link" && target.launchUri) {
    const fallback = target.fallbackUrl;
    const timer = window.setTimeout(() => {
      if (fallback && document.visibilityState === "visible") {
        window.location.assign(fallback);
      }
    }, 1500);
    window.addEventListener("pagehide", () => window.clearTimeout(timer), {
      once: true,
    });
    window.location.assign(target.launchUri);
    return;
  }
  throw new Error(`应用「${app.name}」没有可用入口`);
}

export async function openPreferredApplicationPath(
  navigate: (path: string) => unknown,
  apps: Application[],
  path: string,
): Promise<boolean> {
  if (!path.startsWith("/") || path.startsWith("//")) return false;
  const requestedPath = path.split(/[?#]/u, 1)[0] ?? path;
  const matched = matchApplication(apps, requestedPath);
  const preferred = matched ? primaryTarget(matched.app) : undefined;
  if (!matched || !preferred) return false;
  await openApplicationTarget(navigate, matched.app, preferred, path);
  return true;
}

function standaloneTargetURL(
  app: Application,
  target: ApplicationLaunchTarget,
  requestedPath?: string,
): string {
  if (!requestedPath || !target.url?.startsWith("/open/")) {
    return target.url ?? "";
  }
  const mountPath = innerTarget(app)?.mountPath;
  if (
    mountPath &&
    (requestedPath === mountPath ||
      requestedPath.startsWith(`${mountPath}/`) ||
      requestedPath.startsWith(`${mountPath}?`) ||
      requestedPath.startsWith(`${mountPath}#`))
  ) {
    return `${target.url}${requestedPath.slice(mountPath.length)}`;
  }
  return target.url;
}

function externalBrowserTarget(
  targets: ApplicationLaunchTarget[],
): ApplicationLaunchTarget | undefined {
  const external = (target: ApplicationLaunchTarget) =>
    (target.kind === "external_link" || target.kind === "iframe") &&
    Boolean(target.url);
  return (
    targets.find(
      (target) =>
        target.platform === "desktop" && target.isPrimary && external(target),
    ) ??
    targets.find(
      (target) =>
        target.platform === "web" && target.isPrimary && external(target),
    ) ??
    targets.find(
      (target) => target.platform === "desktop" && external(target),
    ) ??
    targets.find((target) => target.platform === "web" && external(target)) ??
    targets.find(
      (target) => target.kind === "native_link" && Boolean(target.fallbackUrl),
    )
  );
}

function targetOrder(
  a: ApplicationLaunchTarget,
  b: ApplicationLaunchTarget,
): number {
  return Number(b.isPrimary) - Number(a.isPrimary) || a.sortOrder - b.sortOrder;
}
